// client/src/services/adminService.ts

import apiClient from "./api";

// ------------------------------
// TypeScript Interfaces
// ------------------------------

export interface AdminDashboardData {
  totalApplications: number;
  totalUsers: number;
  systemUptime: string;
  activeSessions: number;
  // Add any additional fields if needed.
}

export interface ApplicationReviewData {
  id: string;
  applicantName: string;
  applicationDate: string;
  details: string;
  // Additional fields can be added as required.
}

export interface SystemConfigurationData {
  maintenanceMode: string; // e.g., "on" or "off"
  maxApplications: number;
  // Add more configuration fields as needed.
}

export interface School {
  _id: string;
  name: string;
  location: string;
  contactEmail: string;
  contactPhone: string;
  teachers: string[];
  status: 'pending' | 'paid' | 'active' | 'inactive';
  schoolId: string;
  postalCode: string;
  country: string;
  state: string;
  city: string;
  address: string;
  mpesaPayment?: {
    transactionId: string;
    amount: number;
    paidAt: Date;
    status: 'pending' | 'completed' | 'failed';
  };
}

// ------------------------------
// API Functions
// ------------------------------

/**
 * Fetch overall admin dashboard data.
 * GET /admin/analytics
 */
export const getAdminDashboardData = async (): Promise<AdminDashboardData> => {
  try {
    const response = await apiClient.get("/admin/analytics");
    return {
      totalApplications: response.data.totalUsers || 0,
      totalUsers: response.data.totalUsers || 0,
      systemUptime: "100%", // This is a placeholder since we don't have this metric
      activeSessions: response.data.activeSchools || 0
    };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetch applications pending review.
 * GET /admin/application-reviews
 */
export const getApplicationReviews = async (): Promise<ApplicationReviewData[]> => {
  try {
    const response = await apiClient.get("/admin/application-reviews");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Approve an application.
 * PUT /admin/application/{id}/approve
 */
export const approveApplication = async (id: string): Promise<any> => {
  try {
    const response = await apiClient.put(`/admin/application/${id}/approve`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Reject an application.
 * PUT /admin/application/{id}/reject
 */
export const rejectApplication = async (id: string): Promise<any> => {
  try {
    const response = await apiClient.put(`/admin/application/${id}/reject`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Retrieve the system configuration.
 * GET /admin/system-configuration
 */
export const getSystemConfiguration = async (): Promise<SystemConfigurationData> => {
  try {
    const response = await apiClient.get("/admin/system-configuration");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Update the system configuration.
 * PUT /admin/system-configuration
 */
export const updateSystemConfiguration = async (
  config: SystemConfigurationData
): Promise<SystemConfigurationData> => {
  try {
    const response = await apiClient.put("/admin/system-configuration", config);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetch all schools.
 * GET /admin/schools
 */
export const getSchools = async (): Promise<School[]> => {
  try {
    const response = await apiClient.get("/admin/schools");
    // Backend sends { success: true, data: School[] }
    return response.data.data || [];
  } catch (error) {
    handleApiError(error);
    return []; // Return empty array instead of throwing to prevent UI breakage
  }
};

/**
 * Create a new school.
 * POST /admin/schools
 */
export const createSchool = async (schoolData: Partial<School>): Promise<School> => {
  try {
    const response = await apiClient.post("/admin/schools", schoolData);
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Update school payment status
 * PUT /admin/schools/:id/payment
 */
export const updateSchoolPaymentStatus = async (
  schoolId: string,
  paymentData: {
    transactionId: string;
    amount: number;
    status: 'pending' | 'completed' | 'failed';
  }
): Promise<School> => {
  try {
    const response = await apiClient.put(`/admin/schools/${schoolId}/payment`, paymentData);
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Get school payment status
 * GET /admin/schools/:id/payment
 */
export const getSchoolPaymentStatus = async (schoolId: string): Promise<School['mpesaPayment']> => {
  try {
    const response = await apiClient.get(`/admin/schools/${schoolId}/payment`);
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// ------------------------------
// Utility: Error Handling Function
// ------------------------------

const handleApiError = (error: unknown): void => {
  if (error instanceof Error) {
    console.error("Admin Service Error:", error.message);
  } else {
    console.error("Unexpected Admin Service Error:", error);
  }
};
